import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

/**
 * Dynamic "additional properties" (a.k.a. interaction properties) configured
 * per tenant in Admin → Conversation Management → Additional Properties.
 *
 * Mirrors the reference interaction form (KC2 newInteraction.js) which renders
 * these typed fields underneath the fixed fields. We fetch the agent-visible
 * metadata and, for dropdown fields, their options — degrading gracefully to an
 * empty list if the caller lacks the admin-view permission.
 */
export interface PropertyOption {
  value: string
  label: string
}

export interface AdditionalProperty {
  id: number
  columnName: string
  displayName: string
  inputType: string
  isRequired: boolean
  isReadonly: boolean
  options: PropertyOption[]
  minLength?: number | null
  maxLength?: number | null
}

interface MetadataRow {
  id: number
  columnName: string
  displayName: string
  dataType: string
  inputType: string
  isRequired: boolean
  isVisible: 'agent' | 'client' | 'both'
  // The API serialises this as `isReadOnly` (capital O); accept both spellings.
  isReadOnly?: boolean
  isReadonly?: boolean
  referenceTable?: string | null
  minLength?: number | null
  maxLength?: number | null
}

const BASE = '/admin/conversation-management/additional-properties'
const OPTION_INPUT_TYPES = new Set(['dropdown', 'select', 'multiselect'])

/**
 * Detect geo-cascade child properties (state, city) by display name.
 * These are fetched lazily via parentOption filter, not upfront, to avoid
 * loading thousands of options that aren't relevant without a parent selection.
 */
function isGeoChildProperty(m: MetadataRow): boolean {
  const name = (m.displayName || '').toLowerCase()
  // State/province and city are children in the country → state → city cascade.
  // Country is the root — its options are fetched normally.
  return name.includes('state') || name.includes('province') || name.includes('city')
}

export interface UseAdditionalPropertiesResult {
  properties: AdditionalProperty[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useAdditionalProperties(fetchOnMount = false): UseAdditionalPropertiesResult {
  const [properties, setProperties] = useState<AdditionalProperty[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ metadata: MetadataRow[] }>(`${BASE}/metadata?page=1&limit=1000`)
      // Only fields visible to agents belong on the agent-facing form.
      const visible = (res.metadata ?? []).filter((m) => m.isVisible === 'agent' || m.isVisible === 'both')

      const withOptions = await Promise.all(
        visible.map(async (m): Promise<AdditionalProperty> => {
          let options: PropertyOption[] = []
          // Fetch options for dropdown-style fields, or any field backed by a
          // reference table (the server resolves the reference table for us).
          // Skip geo child properties (state/city) — their options are fetched
          // lazily via parentOption cascade to avoid loading thousands of rows.
          const shouldFetchOptions =
            (OPTION_INPUT_TYPES.has(m.inputType) || (m.referenceTable != null && m.referenceTable !== '')) &&
            !isGeoChildProperty(m)
          if (shouldFetchOptions) {
            try {
              const optRes = await api.get<{ options: { id?: number | string; label?: string; value?: string; name?: string }[] }>(
                `${BASE}/${m.id}/dropdown-options`,
              )
              options = (optRes.options ?? [])
                .map((o) => ({
                  value: String(o.value ?? o.id ?? ''),
                  label: String(o.label ?? o.name ?? o.value ?? o.id ?? ''),
                }))
                .filter((o) => o.value !== '' && o.label !== '')
            } catch {
              options = []
            }
          }
          return {
            id: m.id,
            columnName: m.columnName,
            displayName: m.displayName,
            inputType: m.inputType,
            isRequired: m.isRequired,
            isReadonly: Boolean(m.isReadOnly ?? m.isReadonly),
            options,
            minLength: m.minLength ?? null,
            maxLength: m.maxLength ?? null,
          }
        }),
      )
      setProperties(withOptions)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch additional properties')
      setProperties([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (fetchOnMount) void refetch()
  }, [fetchOnMount, refetch])

  return { properties, loading, error, refetch }
}
